import type { UpdateBookingData, BookingDetailPickup } from "api-client";
import {
  ApiErrorHandler,
  LOOKUP_MASTERS,
  lookupCacheKey,
  lookupItemsToBookingTypeOptions,
  lookupItemsToEquipmentPiecesOptions,
  lookupItemsToExpiresWhenOptions,
  lookupItemsToStorageTypeOptions,
  lookupItemsToSizeSelectStrings,
  lookupItemsToWheeledEquipmentOptions,
  shouldSuppressApiErrorNotification,
} from "api-client";
import {
  CreateBookingCapacityCostDrawer,
  CreateBookingDrawer,
  CreateBookingDropoffStackEquipmentDrawer,
  CreateBookingDropoffWheeledEquipmentDrawer,
  CreateBookingPickupEquipmentDrawer,
  CreateBookingScheduleDrawer,
  useNotificationSnackbar,
  type CreateBookingDropoffStackEquipmentValues,
  type CreateBookingDropoffWheeledEquipmentValues,
  type CreateBookingPickupEquipmentValues,
  type CreateBookingScheduleStepValues,
  type CreateBookingStepOneValues,
  useLookups,
} from "design-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBookingWizardCustomers } from "../../../shared/hooks/useBookingWizardCustomers";
import { lookupService } from "../../../shared/services/lookup.service";
import { bookingsService } from "../../../shared/services/bookings.service";
import { useAppDispatch } from "../../../shared/store/hook";
import { updateBooking } from "../../../shared/store/bookings";
import {
  buildPickupDetailEquipmentOptionId,
  buildPickupEquipmentOptions,
  ensurePickupEquipmentOptionInList,
  findPickupStaticEquipmentById,
  formatPickupMultiPieceWireRowLabel,
  isPickupDetailEquipmentOptionId,
  isPickupMultiPieceWireRowOptionId,
  resolvePickupStaticEquipmentStorageType,
} from "../constants/pickup-static-equipment.constants";
import {
  bookingWizardCustomerOptions,
  bookingWizardLocationStorageTypes,
  bookingWizardLocationOptions,
  buildCapacityCostLabels,
  buildOperatingHoursDisplay,
  stripBookingWizardLocationLabel,
  findBookingWizardCustomer,
  findBookingWizardLocation,
  editHydratedSealCaptureRequire,
  resolveBookingWizardLocationStorageType,
  resolveBookingWizardLocationTimeZone,
  resolveDefaultDayRate,
  sealCaptureRequiredByDefault,
  sealCaptureToggleLabel,
  stepOneSealContextChanged,
} from "../utils/booking-wizard-options";
import { mapBookingDetailToEditWizard, type EditBookingLoadedFees } from "../utils/booking-detail.mapper";
import { resolveIanaTimeZone, scheduleStepToDraftFields } from "../utils/create-booking-payload.mapper";
import { buildUpdateBookingRequest } from "../utils/update-booking-payload.mapper";

const EDIT_DRAWER_TITLE_BASE = "Edit booking";
const WIZARD_LOOKUP_MASTERS = [
  LOOKUP_MASTERS.bookingType,
  LOOKUP_MASTERS.bookingStorageType,
  LOOKUP_MASTERS.containerSize,
  LOOKUP_MASTERS.trailerSize,
  LOOKUP_MASTERS.equipmentType,
  LOOKUP_MASTERS.enforcementMode,
  LOOKUP_MASTERS.equipmentPieces,
  LOOKUP_MASTERS.expiresWhen,
] as const;

type WizardFace = "one" | "dropoffStackEquipment" | "equipmentSeal" | "schedule" | "capacityCost";

export interface EditBookingWizardProps {
  open: boolean;
  onClose: () => void;
  bookingId: number | null;
  operatorId: number;
  onBookingUpdated?: (data: UpdateBookingData, message: string) => void;
}

/**
 * AV-44 — Edit booking wizard: reuses create-booking drawer steps with pre-fill from GET detail.
 */
export function EditBookingWizard({
  open,
  onClose,
  bookingId,
  operatorId,
  onBookingUpdated,
}: EditBookingWizardProps) {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotificationSnackbar();
  const { customers, loading: customersLoading, error: customersLoadError } = useBookingWizardCustomers({
    enabled: open,
    operatorId,
  });
  const {
    byKey: lookupByKey,
    loading: lookupsLoading,
    error: lookupsLoadError,
  } = useLookups(lookupService, WIZARD_LOOKUP_MASTERS, { enabled: open });

  const bookingTypeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.bookingType)] ?? [];
    return lookupItemsToBookingTypeOptions(items);
  }, [lookupByKey]);

  const storageTypeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.bookingStorageType)] ?? [];
    return lookupItemsToStorageTypeOptions(items);
  }, [lookupByKey]);

  const containerSizeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.containerSize)] ?? [];
    return lookupItemsToSizeSelectStrings(items);
  }, [lookupByKey]);

  const trailerSizeOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.trailerSize)] ?? [];
    return lookupItemsToSizeSelectStrings(items);
  }, [lookupByKey]);

  const droppedEquipmentOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.equipmentType)] ?? [];
    return lookupItemsToWheeledEquipmentOptions(items);
  }, [lookupByKey]);

  const equipmentPiecesOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.equipmentPieces)] ?? [];
    return lookupItemsToEquipmentPiecesOptions(items);
  }, [lookupByKey]);

  const expiresWhenOptions = useMemo(() => {
    const items = lookupByKey[lookupCacheKey(LOOKUP_MASTERS.expiresWhen)] ?? [];
    return lookupItemsToExpiresWhenOptions(items);
  }, [lookupByKey]);

  const equipmentPiecesLookupItems = useMemo(
    () => lookupByKey[lookupCacheKey(LOOKUP_MASTERS.equipmentPieces)] ?? [],
    [lookupByKey],
  );

  const expiresWhenLookupItems = useMemo(
    () => lookupByKey[lookupCacheKey(LOOKUP_MASTERS.expiresWhen)] ?? [],
    [lookupByKey],
  );

  const enforcementModeLookupItems = useMemo(
    () => lookupByKey[lookupCacheKey(LOOKUP_MASTERS.enforcementMode)] ?? [],
    [lookupByKey],
  );

  const combinedCatalogError = customersLoadError ?? lookupsLoadError;
  const catalogsLoading = customersLoading || lookupsLoading;

  const [face, setFace] = useState<WizardFace>("one");
  const [stepOneDraft, setStepOneDraft] = useState<CreateBookingStepOneValues | null>(null);
  const [pickupEquipmentDraft, setPickupEquipmentDraft] = useState<CreateBookingPickupEquipmentValues | null>(null);
  const [dropoffStackEquipmentDraft, setDropoffStackEquipmentDraft] =
    useState<CreateBookingDropoffStackEquipmentValues | null>(null);
  const [dropoffWheeledEquipmentDraft, setDropoffWheeledEquipmentDraft] =
    useState<CreateBookingDropoffWheeledEquipmentValues | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState<CreateBookingScheduleStepValues | null>(null);
  const [loadedFees, setLoadedFees] = useState<EditBookingLoadedFees | null>(null);
  const [dayRateDraft, setDayRateDraft] = useState<number | null>(null);
  const [pickupEquipmentDetails, setPickupEquipmentDetails] = useState<string | null>(null);
  const [pickupDetailSnapshot, setPickupDetailSnapshot] = useState<BookingDetailPickup | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [stepOneCustomerId, setStepOneCustomerId] = useState<string | null>(null);
  const [bookingNumber, setBookingNumber] = useState<string | null>(null);
  const [hydratedSealCaptureEnabled, setHydratedSealCaptureEnabled] = useState<boolean | null>(null);
  const hydratedMustArriveByRef = useRef<Date | null>(null);
  const hydratedScheduledRef = useRef<Date | null>(null);

  const editDrawerTitle = useMemo(() => {
    const number = bookingNumber?.trim();
    return number ? `${EDIT_DRAWER_TITLE_BASE} - ${number}` : EDIT_DRAWER_TITLE_BASE;
  }, [bookingNumber]);

  const customerOptions = useMemo(
    () =>
      bookingWizardCustomerOptions(customers, {
        mode: "edit",
        selectedCustomerId: stepOneCustomerId,
      }),
    [customers, stepOneCustomerId],
  );

  const stepOneInitialValues = useMemo(() => {
    if (!stepOneDraft) return null;
    const customerId = hydrated
      ? (stepOneCustomerId ?? "")
      : (stepOneCustomerId ?? stepOneDraft.customerId);
    return {
      ...stepOneDraft,
      customerId,
    };
  }, [hydrated, stepOneCustomerId, stepOneDraft]);

  const activeCustomerId = hydrated ? stepOneCustomerId : stepOneDraft?.customerId ?? null;

  const scopedLocationOptions = useMemo(
    () => bookingWizardLocationOptions(customers, activeCustomerId),
    [activeCustomerId, customers],
  );

  const locationStorageTypes = useMemo(
    () => bookingWizardLocationStorageTypes(customers, activeCustomerId),
    [activeCustomerId, customers],
  );

  const locationTimeZone = useMemo(() => {
    const customerId = activeCustomerId;
    const locationId = stepOneDraft?.locationId;
    if (!customerId || !locationId) return null;
    return resolveBookingWizardLocationTimeZone(customers, customerId, locationId);
  }, [activeCustomerId, customers, stepOneDraft?.locationId]);

  const capacityCostTimeZone = useMemo(() => resolveIanaTimeZone(locationTimeZone), [locationTimeZone]);

  const selectedCustomer = useMemo(() => {
    const customerId = activeCustomerId;
    return findBookingWizardCustomer(customers, customerId);
  }, [activeCustomerId, customers]);

  const selectedLocation = useMemo(
    () => findBookingWizardLocation(selectedCustomer, stepOneDraft?.locationId),
    [selectedCustomer, stepOneDraft?.locationId],
  );

  const operatingHoursDisplay = useMemo(
    () => buildOperatingHoursDisplay(selectedLocation?.operatingHours),
    [selectedLocation?.operatingHours],
  );

  const pickupLocationStorageType = useMemo(
    () => resolveBookingWizardLocationStorageType(selectedLocation),
    [selectedLocation],
  );

  const pickupEquipmentOptions = useMemo(() => {
    const isPickupFlow = stepOneDraft?.bookingType === "pickup";
    const isPickupMultipiece =
      isPickupFlow && stepOneDraft?.equipmentPieces === "multiple_pieces";
    const storageFilter = isPickupFlow
      ? null
      : stepOneDraft?.storageType ?? pickupLocationStorageType;
    let options = buildPickupEquipmentOptions(
      storageFilter,
      isPickupMultipiece ? "containers_only" : "all",
    );
    const detailLabel = pickupEquipmentDetails?.trim();
    const detailOptionId = bookingId != null ? buildPickupDetailEquipmentOptionId(bookingId) : null;
    if (
      detailLabel &&
      detailOptionId &&
      !options.some((option) => option.value === detailOptionId)
    ) {
      options = [{ value: detailOptionId, label: detailLabel }, ...options];
    }
    const multiPieceRows = pickupDetailSnapshot?.multiPieceEquipmentDetails ?? [];
    pickupEquipmentDraft?.equipmentIds.forEach((equipmentId, index) => {
      if (!isPickupMultiPieceWireRowOptionId(equipmentId)) return;
      const wireRow = multiPieceRows[index];
      if (!wireRow) return;
      const label = formatPickupMultiPieceWireRowLabel(wireRow);
      if (!options.some((option) => option.value === equipmentId)) {
        options = [{ value: equipmentId, label }, ...options];
      }
    });
    return ensurePickupEquipmentOptionInList(
      options,
      pickupEquipmentDraft?.equipmentId,
      pickupEquipmentDetails,
    );
  }, [
    bookingId,
    pickupDetailSnapshot?.multiPieceEquipmentDetails,
    pickupEquipmentDetails,
    pickupEquipmentDraft?.equipmentId,
    pickupEquipmentDraft?.equipmentIds,
    pickupLocationStorageType,
    stepOneDraft?.bookingType,
    stepOneDraft?.equipmentPieces,
    stepOneDraft?.storageType,
  ]);

  const selectedPickupStaticEquipment = useMemo(
    () => findPickupStaticEquipmentById(pickupEquipmentDraft?.equipmentId),
    [pickupEquipmentDraft?.equipmentId],
  );

  const capacityCostLabels = useMemo(
    () =>
      buildCapacityCostLabels({
        location: selectedLocation,
        storageType:
          stepOneDraft?.bookingType === "pickup"
            ? resolvePickupStaticEquipmentStorageType(selectedPickupStaticEquipment)
            : (stepOneDraft?.storageType ?? null),
        bookingType: stepOneDraft?.bookingType ?? null,
        equipmentPieces: stepOneDraft?.equipmentPieces ?? null,
        scheduledDateTime: scheduleDraft?.scheduledDateTime ?? null,
        locationTimeZone: capacityCostTimeZone,
        enforcementModeLookupItems,
      }),
    [
      capacityCostTimeZone,
      enforcementModeLookupItems,
      scheduleDraft?.scheduledDateTime,
      selectedPickupStaticEquipment,
      selectedLocation,
      stepOneDraft?.bookingType,
      stepOneDraft?.equipmentPieces,
      stepOneDraft?.storageType,
    ],
  );

  const multipieceDefaultDayRate = useMemo(
    () =>
      resolveDefaultDayRate(
        selectedLocation,
        stepOneDraft?.storageType ?? null,
        stepOneDraft?.bookingType ?? null,
      ),
    [selectedLocation, stepOneDraft?.bookingType, stepOneDraft?.storageType],
  );

  const isStackMultiplePieces =
    stepOneDraft?.bookingType === "dropoff" &&
    stepOneDraft?.storageType === "stack" &&
    stepOneDraft?.equipmentPieces === "multiple_pieces";
  const isPickupMultiplePieces =
    stepOneDraft?.bookingType === "pickup" && stepOneDraft?.equipmentPieces === "multiple_pieces";
  const isMultipieceMabOnlySchedule = isStackMultiplePieces || isPickupMultiplePieces;
  const isMultipieceScheduleFinalize = isStackMultiplePieces || isPickupMultiplePieces;
  const isDropoffScheduleOptional =
    stepOneDraft?.bookingType === "dropoff" && !isStackMultiplePieces;
  const isPickupScheduleOptional = stepOneDraft?.bookingType === "pickup";
  const isScheduleOptional = isDropoffScheduleOptional || isPickupScheduleOptional;

  const resetWizardState = useCallback(() => {
    setFace("one");
    setStepOneDraft(null);
    setPickupEquipmentDraft(null);
    setDropoffStackEquipmentDraft(null);
    setDropoffWheeledEquipmentDraft(null);
    setScheduleDraft(null);
    setLoadedFees(null);
    setDayRateDraft(null);
    setPickupEquipmentDetails(null);
    setPickupDetailSnapshot(null);
    setSubmitting(false);
    setDetailLoading(false);
    setHydrated(false);
    setStepOneCustomerId(null);
    setBookingNumber(null);
    setHydratedSealCaptureEnabled(null);
    hydratedMustArriveByRef.current = null;
    hydratedScheduledRef.current = null;
  }, []);

  const onCloseRef = useRef(onClose);
  const showNotificationRef = useRef(showNotification);
  useEffect(() => {
    onCloseRef.current = onClose;
    showNotificationRef.current = showNotification;
  }, [onClose, showNotification]);

  useEffect(() => {
    if (open) return;
    resetWizardState();
  }, [open, resetWizardState]);

  useEffect(() => {
    if (!open || bookingId == null || lookupsLoading) return;

    let cancelled = false;
    setDetailLoading(true);
    setHydrated(false);

    void (async () => {
      try {
        const response = await bookingsService.getBookingDetail(bookingId);
        if (cancelled) return;
        const hydration = mapBookingDetailToEditWizard(response.data, {
          equipmentPiecesLookup: equipmentPiecesLookupItems,
          expiresWhenLookup: expiresWhenLookupItems,
        });
        setBookingNumber(response.data.bookingNumber?.trim() || null);
        setStepOneDraft(hydration.stepOne);
        setStepOneCustomerId(hydration.stepOne.customerId);
        setScheduleDraft(hydration.schedule);
        hydratedMustArriveByRef.current = hydration.schedule.mustArriveBy;
        hydratedScheduledRef.current = hydration.schedule.scheduledDateTime;
        setPickupEquipmentDraft(hydration.pickupEquipment);
        setDropoffStackEquipmentDraft(hydration.dropoffStack);
        setDropoffWheeledEquipmentDraft(hydration.dropoffWheeled);
        setLoadedFees(hydration.loadedFees);
        setDayRateDraft(hydration.prefillDayRate);
        setPickupEquipmentDetails(hydration.pickupEquipmentDetails);
        setPickupDetailSnapshot(hydration.pickupDetailSnapshot);
        setHydratedSealCaptureEnabled(hydration.sealCaptureEnabled);
        setFace("one");
        setHydrated(true);
      } catch (e: unknown) {
        if (cancelled) return;
        if (shouldSuppressApiErrorNotification(e)) {
          resetWizardState();
          onCloseRef.current();
          return;
        }
        resetWizardState();
        onCloseRef.current();
        showNotificationRef.current({
          variant: "error",
          title: "Cannot edit booking",
          description: ApiErrorHandler.extractAxiosError(e, "Failed to load booking."),
        });
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    bookingId,
    equipmentPiecesLookupItems,
    expiresWhenLookupItems,
    lookupsLoading,
    open,
    resetWizardState,
  ]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetWizardState();
    onClose();
  }, [onClose, resetWizardState, submitting]);

  const handleStepOneNext = useCallback(
    (values: CreateBookingStepOneValues) => {
      setStepOneDraft((prev) => {
        const nextStepOne: CreateBookingStepOneValues = {
          ...values,
          // Pickup step-one drawer omits storageType; preserve hydrated value for edit pre-fill.
          storageType:
            values.bookingType === "pickup" ? (prev?.storageType ?? values.storageType) : values.storageType,
          // Dropoff wheeled hides equipment pieces on step one; keep hydrated slug (FR-AV186-003).
          equipmentPieces: values.equipmentPieces ?? prev?.equipmentPieces ?? null,
        };

        if (prev) {
          clearEditEquipmentDraftsOnStepOneAdvance(prev, nextStepOne, {
            clearPickup: setPickupEquipmentDraft,
            clearDropoffStack: setDropoffStackEquipmentDraft,
            clearDropoffWheeled: setDropoffWheeledEquipmentDraft,
          });

          if (stepOneSealContextChanged(prev, nextStepOne)) {
            const requireSealCapture = sealCaptureRequiredByDefault(
              findBookingWizardCustomer(customers, nextStepOne.customerId),
              nextStepOne.bookingType,
            );
            setPickupEquipmentDraft((draft) => (draft ? { ...draft, requireSealCapture } : draft));
            setDropoffStackEquipmentDraft((draft) => (draft ? { ...draft, requireSealCapture } : draft));
            setDropoffWheeledEquipmentDraft((draft) => (draft ? { ...draft, requireSealCapture } : draft));
          }
        }

        return nextStepOne;
      });
      setStepOneCustomerId(values.customerId);
      if (values.bookingType === "dropoff" && values.storageType === "stack") {
        setFace("dropoffStackEquipment");
        return;
      }
      setFace("equipmentSeal");
    },
    [customers],
  );

  const handleDropoffStackEquipmentBack = useCallback(() => setFace("one"), []);
  const handleDropoffStackEquipmentNext = useCallback((values: CreateBookingDropoffStackEquipmentValues) => {
    setDropoffStackEquipmentDraft(values);
    setFace("schedule");
  }, []);
  const handleEquipmentSealBack = useCallback(() => setFace("one"), []);
  const handlePickupEquipmentNext = useCallback((values: CreateBookingPickupEquipmentValues) => {
    setPickupEquipmentDraft(values);
    setFace("schedule");
  }, []);
  const handleDropoffWheeledEquipmentNext = useCallback((values: CreateBookingDropoffWheeledEquipmentValues) => {
    setDropoffWheeledEquipmentDraft(values);
    setFace("schedule");
  }, []);

  const handleScheduleBack = useCallback(
    (values: CreateBookingScheduleStepValues) => {
      setScheduleDraft(values);
      hydratedScheduledRef.current = values.scheduledDateTime;
      hydratedMustArriveByRef.current = values.mustArriveBy;
      if (stepOneDraft?.bookingType === "dropoff" && stepOneDraft.storageType === "stack") {
        setFace("dropoffStackEquipment");
        return;
      }
      setFace("equipmentSeal");
    },
    [stepOneDraft?.bookingType, stepOneDraft?.storageType],
  );

  const scheduleScheduledDateTime = scheduleDraft?.scheduledDateTime ?? hydratedScheduledRef.current;
  const scheduleMustArriveBy = scheduleDraft?.mustArriveBy ?? hydratedMustArriveByRef.current;

  const handleCapacityCostBack = useCallback(() => setFace("schedule"), []);
  const handleCustomerIdChange = useCallback((customerId: string | null) => setStepOneCustomerId(customerId), []);

  const pickupEquipmentLabel = useMemo(() => {
    if (!pickupEquipmentDraft) return null;
    const singleId = pickupEquipmentDraft.equipmentId;
    if (singleId) {
      if (isPickupDetailEquipmentOptionId(singleId)) {
        return pickupEquipmentDetails?.trim() ?? null;
      }
      return pickupEquipmentOptions.find((option) => option.value === singleId)?.label ?? singleId;
    }
    const firstMultiId = pickupEquipmentDraft.equipmentIds[0];
    if (firstMultiId) {
      return pickupEquipmentOptions.find((option) => option.value === firstMultiId)?.label ?? firstMultiId;
    }
    return null;
  }, [pickupEquipmentDetails, pickupEquipmentDraft, pickupEquipmentOptions]);

  const executeUpdateBooking = useCallback(
    async (schedule: CreateBookingScheduleStepValues) => {
      if (!stepOneDraft || !bookingId || submitting) return;

      if (!isMultipieceScheduleFinalize && capacityCostLabels.action === "end") {
        showNotification({
          variant: "error",
          title: "Booking not updated",
          description: "Booking was not updated due to capacity.",
        });
        return;
      }

      if (stepOneDraft.bookingType === "dropoff") {
        if (stepOneDraft.storageType === "stack" && !dropoffStackEquipmentDraft) return;
        if (stepOneDraft.storageType === "wheeled" && !dropoffWheeledEquipmentDraft) return;
      }
      if (stepOneDraft.bookingType === "pickup" && !pickupEquipmentDraft) return;

      const resolvedDayRate = isMultipieceScheduleFinalize
        ? multipieceDefaultDayRate
        : (dayRateDraft ?? capacityCostLabels.defaultDayRate);

      const body = buildUpdateBookingRequest({
        bookingId,
        stepOne: stepOneDraft,
        ...scheduleStepToDraftFields(schedule),
        locationTimeZone,
        costPayload: { dayRate: resolvedDayRate },
        loadedFees,
        equipmentPiecesLookup: equipmentPiecesLookupItems,
        expiresWhenLookup: expiresWhenLookupItems,
        pickupEquipment: pickupEquipmentDraft,
        dropoffStack: dropoffStackEquipmentDraft,
        dropoffWheeled: dropoffWheeledEquipmentDraft,
        pickupEquipmentLabel,
        pickupEquipmentDetails,
        pickupDetailSnapshot,
        pickupStaticEquipment: selectedPickupStaticEquipment,
      });

      setSubmitting(true);
      try {
        const response = await dispatch(updateBooking(body)).unwrap();
        onBookingUpdated?.(response.data, response.message);
        resetWizardState();
        onClose();
      } catch (message) {
        showNotification({
          variant: "error",
          title: "Could not update booking",
          description: typeof message === "string" ? message : "Failed to update booking",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      bookingId,
      capacityCostLabels.action,
      capacityCostLabels.defaultDayRate,
      dayRateDraft,
      dispatch,
      dropoffStackEquipmentDraft,
      dropoffWheeledEquipmentDraft,
      equipmentPiecesLookupItems,
      expiresWhenLookupItems,
      isMultipieceScheduleFinalize,
      loadedFees,
      multipieceDefaultDayRate,
      locationTimeZone,
      onBookingUpdated,
      onClose,
      pickupDetailSnapshot,
      pickupEquipmentDetails,
      pickupEquipmentDraft,
      pickupEquipmentLabel,
      resetWizardState,
      selectedLocation,
      selectedPickupStaticEquipment,
      showNotification,
      stepOneDraft,
      submitting,
    ],
  );

  const handleUpdateBooking = useCallback(async () => {
    if (!scheduleDraft) return;
    await executeUpdateBooking(scheduleDraft);
  }, [executeUpdateBooking, scheduleDraft]);

  const handleScheduleNext = useCallback(
    async (values: CreateBookingScheduleStepValues) => {
      if (isMultipieceScheduleFinalize) {
        hydratedScheduledRef.current = values.scheduledDateTime;
        hydratedMustArriveByRef.current = values.mustArriveBy;
        setScheduleDraft(values);
        await executeUpdateBooking(values);
        return;
      }
      if (!isScheduleOptional && !values.scheduledDateTime && !values.scheduledDatePart) return;
      hydratedScheduledRef.current = values.scheduledDateTime;
      hydratedMustArriveByRef.current = values.mustArriveBy;
      setScheduleDraft(values);
      setFace("capacityCost");
    },
    [executeUpdateBooking, isMultipieceScheduleFinalize, isScheduleOptional],
  );

  const scheduleLocationLabel = useMemo(() => {
    if (!stepOneDraft) return "Location name";
    const loc = scopedLocationOptions.find((o) => o.value === stepOneDraft.locationId);
    if (loc) return stripBookingWizardLocationLabel(loc.label);
    return "Location name";
  }, [stepOneDraft, scopedLocationOptions]);

  const isPickup = stepOneDraft?.bookingType === "pickup";
  const isDropoff = stepOneDraft?.bookingType === "dropoff";
  const isDropoffWheeled = Boolean(isDropoff && stepOneDraft?.storageType === "wheeled");
  const wizardReady = open && hydrated && !detailLoading && bookingId != null;

  const pickupSealToggleLabel = sealCaptureToggleLabel("pickup");
  const dropoffSealToggleLabel = sealCaptureToggleLabel("dropoff");
  const pickupInitialRequireSealCapture = editHydratedSealCaptureRequire(
    pickupEquipmentDraft,
    hydratedSealCaptureEnabled,
  );
  const dropoffStackInitialRequireSealCapture = editHydratedSealCaptureRequire(
    dropoffStackEquipmentDraft,
    hydratedSealCaptureEnabled,
  );
  const dropoffWheeledInitialRequireSealCapture = editHydratedSealCaptureRequire(
    dropoffWheeledEquipmentDraft,
    hydratedSealCaptureEnabled,
  );

  return (
    <>
      <CreateBookingDrawer
        open={wizardReady && face === "one"}
        onClose={handleClose}
        drawerTitle={editDrawerTitle}
        customerOptions={customerOptions}
        scopedLocationOptions={scopedLocationOptions}
        customersLoading={catalogsLoading || detailLoading}
        customersLoadError={combinedCatalogError}
        initialValues={stepOneInitialValues}
        bookingTypeOptions={bookingTypeOptions}
        storageTypeOptions={storageTypeOptions}
        equipmentPiecesOptions={equipmentPiecesOptions}
        locationStorageTypes={locationStorageTypes}
        catalogOptionsLoading={lookupsLoading}
        onCustomerIdChange={handleCustomerIdChange}
        onNext={handleStepOneNext}
      />
      <CreateBookingDropoffStackEquipmentDrawer
        open={wizardReady && face === "dropoffStackEquipment"}
        onClose={handleClose}
        drawerTitle={editDrawerTitle}
        onBack={handleDropoffStackEquipmentBack}
        onNext={handleDropoffStackEquipmentNext}
        equipmentPieces={stepOneDraft?.equipmentPieces ?? "one_piece"}
        containerSizeOptions={containerSizeOptions}
        expiresWhenOptions={expiresWhenOptions}
        initialIsReefer={dropoffStackEquipmentDraft?.isReefer ?? null}
        initialContainerNumber={dropoffStackEquipmentDraft?.containerNumber ?? ""}
        initialContainerSize={dropoffStackEquipmentDraft?.containerSize ?? ""}
        initialEquipmentOwner={dropoffStackEquipmentDraft?.equipmentOwner ?? ""}
        initialQuantity={dropoffStackEquipmentDraft?.quantity ?? null}
        initialExpiresWhen={dropoffStackEquipmentDraft?.expiresWhen ?? null}
        initialRequireSealCapture={dropoffStackInitialRequireSealCapture}
        sealCaptureToggleLabel={dropoffSealToggleLabel}
      />
      <CreateBookingPickupEquipmentDrawer
        open={wizardReady && face === "equipmentSeal" && Boolean(isPickup)}
        onClose={handleClose}
        drawerTitle={editDrawerTitle}
        onBack={handleEquipmentSealBack}
        onNext={handlePickupEquipmentNext}
        equipmentPieces={stepOneDraft?.equipmentPieces ?? "one_piece"}
        equipmentOptions={pickupEquipmentOptions}
        equipmentLoading={false}
        expiresWhenOptions={expiresWhenOptions}
        initialEquipmentId={pickupEquipmentDraft?.equipmentId ?? null}
        initialEquipmentIds={pickupEquipmentDraft?.equipmentIds ?? []}
        initialQuantity={pickupEquipmentDraft?.quantity ?? null}
        initialExpiresWhen={pickupEquipmentDraft?.expiresWhen ?? null}
        initialRequireSealCapture={pickupInitialRequireSealCapture}
        sealCaptureToggleLabel={pickupSealToggleLabel}
      />
      <CreateBookingDropoffWheeledEquipmentDrawer
        open={wizardReady && face === "equipmentSeal" && isDropoffWheeled}
        onClose={handleClose}
        drawerTitle={editDrawerTitle}
        onBack={handleEquipmentSealBack}
        onNext={handleDropoffWheeledEquipmentNext}
        containerSizeSelectOptions={containerSizeOptions}
        trailerSizeSelectOptions={trailerSizeOptions}
        droppedEquipmentOptions={droppedEquipmentOptions}
        initialDroppedOffEquipment={dropoffWheeledEquipmentDraft?.droppedOffEquipment ?? null}
        initialChassisId={dropoffWheeledEquipmentDraft?.chassisId ?? ""}
        initialLicensePlate={dropoffWheeledEquipmentDraft?.licensePlate ?? ""}
        initialEquipmentOwner={dropoffWheeledEquipmentDraft?.equipmentOwner ?? ""}
        initialContainerIsReefer={dropoffWheeledEquipmentDraft?.containerIsReefer ?? null}
        initialContainerNumber={dropoffWheeledEquipmentDraft?.containerNumber ?? ""}
        initialContainerSize={dropoffWheeledEquipmentDraft?.containerSize ?? ""}
        initialContainerEquipmentOwner={dropoffWheeledEquipmentDraft?.containerEquipmentOwner ?? ""}
        initialTrailerIsReefer={dropoffWheeledEquipmentDraft?.trailerIsReefer ?? null}
        initialTrailerNumber={dropoffWheeledEquipmentDraft?.trailerNumber ?? ""}
        initialTrailerSize={dropoffWheeledEquipmentDraft?.trailerSize ?? ""}
        initialTrailerLicensePlate={dropoffWheeledEquipmentDraft?.trailerLicensePlate ?? ""}
        initialTrailerEquipmentOwner={dropoffWheeledEquipmentDraft?.trailerEquipmentOwner ?? ""}
        initialRequireSealCapture={dropoffWheeledInitialRequireSealCapture}
        sealCaptureToggleLabel={dropoffSealToggleLabel}
      />
      <CreateBookingScheduleDrawer
        open={wizardReady && face === "schedule"}
        onClose={handleClose}
        drawerTitle={editDrawerTitle}
        scheduleMode="edit"
        onBack={handleScheduleBack}
        onNext={handleScheduleNext}
        locationLabel={scheduleLocationLabel}
        operatingHours={operatingHoursDisplay}
        locationTimeZone={locationTimeZone}
        initialScheduledDateTime={scheduleScheduledDateTime}
        initialMustArriveBy={scheduleMustArriveBy}
        initialScheduledDatePart={scheduleDraft?.scheduledDatePart ?? null}
        initialScheduledTimePart={scheduleDraft?.scheduledTimePart ?? null}
        initialMustArriveDatePart={scheduleDraft?.mustArriveDatePart ?? null}
        initialMustArriveTimePart={scheduleDraft?.mustArriveTimePart ?? null}
        showScheduledDateTime={!isMultipieceMabOnlySchedule}
        showOperatingHours
        scheduledDateTimeRequired={!isScheduleOptional}
        mustArriveByRequired={!isScheduleOptional && !isMultipieceMabOnlySchedule}
        trailingLabel={isMultipieceScheduleFinalize ? "Update booking" : undefined}
        trailingShowNextIcon={isMultipieceScheduleFinalize ? false : undefined}
        submitting={isMultipieceScheduleFinalize ? submitting : false}
        submittingLabel="Updating…"
      />
      <CreateBookingCapacityCostDrawer
        open={wizardReady && face === "capacityCost" && !isPickupMultiplePieces}
        onClose={handleClose}
        drawerTitle={editDrawerTitle}
        onBack={handleCapacityCostBack}
        onCreateBooking={handleUpdateBooking}
        submitting={submitting}
        submittingLabel="Updating…"
        primaryActionLabel="Update booking"
        primaryCapacityLabel={capacityCostLabels.primaryCapacityLabel}
        stackSpotsFullLabel={capacityCostLabels.stackSpotsFullLabel}
        remainingSpotsLabel={capacityCostLabels.remainingSpotsLabel}
        remainingSpotsTone={capacityCostLabels.remainingSpotsTone}
        wheeledSpotsFullLabel={capacityCostLabels.wheeledSpotsFullLabel}
        wheeledRemainingSpotsLabel={capacityCostLabels.wheeledRemainingSpotsLabel}
        wheeledRemainingSpotsTone={capacityCostLabels.wheeledRemainingSpotsTone}
        dayRate={dayRateDraft}
        onDayRateChange={setDayRateDraft}
        showCostEstimate={capacityCostLabels.showCostEstimate}
        warningMessage={capacityCostLabels.warningMessage}
        warningMessageTone={capacityCostLabels.warningMessageTone ?? undefined}
      />
    </>
  );
}

interface StepOneEquipmentDraftClearers {
  clearPickup: (value: null) => void;
  clearDropoffStack: (value: null) => void;
  clearDropoffWheeled: (value: null) => void;
}

/**
 * AV-44 edit — keep equipment + schedule pre-fill when only customer/location changes
 * if booking type (and dropoff storage branch) stays the same.
 */
function clearEditEquipmentDraftsOnStepOneAdvance(
  prev: CreateBookingStepOneValues,
  values: CreateBookingStepOneValues,
  clearers: StepOneEquipmentDraftClearers,
): void {
  if (prev.bookingType !== values.bookingType) {
    clearers.clearPickup(null);
    clearers.clearDropoffStack(null);
    clearers.clearDropoffWheeled(null);
    return;
  }

  if (values.bookingType === "pickup") {
    if (prev.equipmentPieces !== values.equipmentPieces) {
      clearers.clearPickup(null);
    }
    return;
  }

  if (prev.storageType === values.storageType) {
    return;
  }

  if (values.storageType === "stack") {
    clearers.clearDropoffWheeled(null);
  } else if (values.storageType === "wheeled") {
    clearers.clearDropoffStack(null);
  } else {
    clearers.clearDropoffStack(null);
    clearers.clearDropoffWheeled(null);
  }
}
